/* ============================================================
   AURELIUM — theme interactions
   Cart (AJAX), drawer/menu, variants, gallery, reveal, toast
   ============================================================ */
(function () {
  'use strict';

  const routes = window.routes || {};
  const money = (cents) => {
    // Shopify renders formatted prices server-side; for JS-built nodes (variant
    // switches) we format client-side using the shop's money_format pattern.
    const fmt = window.themeSettings && window.themeSettings.moneyFormat;
    const noDecimals = fmt && /amount_no_decimals/.test(fmt);
    const value = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2
    });
    // matches {{amount}}, {{ amount_with_comma_separator }}, {{amount_no_decimals}}, etc.
    return fmt ? fmt.replace(/\{\{\s*amount[^}]*\}\}/, value) : '₹' + value;
  };

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.prototype.slice.call(c.querySelectorAll(s));

  /* ---------- Scroll lock ---------- */
  let lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('body-lock', lockCount > 0);
  }

  /* ---------- Toast ---------- */
  let toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    requestAnimationFrame(() => toastEl.classList.add('is-show'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-show'), 2600);
  }

  /* ---------- Generic open/close panels (mobile nav + cart drawer) ---------- */
  function bindPanel(panel, openSelectors) {
    if (!panel) return;
    const open = () => { panel.classList.add('is-open'); lockScroll(true);
      const f = panel.querySelector('[data-panel-close]'); if (f) f.focus(); };
    const close = () => { panel.classList.remove('is-open'); lockScroll(false); };
    $$(openSelectors).forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    $$('[data-panel-close]', panel).forEach((b) => b.addEventListener('click', close));
    const overlay = panel.querySelector('[data-panel-overlay]');
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && panel.classList.contains('is-open')) close(); });
    return { open, close };
  }

  const mobileNav = bindPanel($('#MobileNav'), '[data-menu-open]');
  const cartDrawerCtl = bindPanel($('#CartDrawer'), '[data-cart-open]');

  /* ---------- Cart open behaviour ---------- */
  function openCart() {
    if (window.themeSettings && window.themeSettings.cartType === 'page') {
      window.location.href = routes.cart_url || '/cart';
      return;
    }
    if (cartDrawerCtl) cartDrawerCtl.open();
  }

  /* ---------- Update cart count bubbles ---------- */
  function setCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  /* ---------- Fetch + render cart drawer contents ---------- */
  async function refreshCart() {
    try {
      const res = await fetch(`${routes.cart_url || '/cart'}?section_id=cart-drawer`);
      if (!res.ok) return;
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('[data-cart-contents]');
      const target = $('[data-cart-contents]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const countEl = html.querySelector('[data-cart-count]');
      if (countEl) setCartCount(parseInt(countEl.textContent, 10) || 0);
      bindQtySteppers(document);
    } catch (e) { /* noop */ }
  }

  /* ---------- Add to cart ---------- */
  async function addToCart(form, btn) {
    const original = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.dataset.loading = 'true'; btn.innerHTML = 'Adding…'; }
    try {
      const formData = new FormData(form);
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) { toast(data.description || data.message || 'Could not add to cart'); return; }
      await refreshCart();
      toast(`${data.product_title || 'Item'} added to your cart`);
      openCart();
    } catch (e) {
      toast('There was an error adding to cart');
    } finally {
      if (btn) { btn.disabled = false; delete btn.dataset.loading; btn.innerHTML = original; }
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, form.querySelector('[data-add-button]'));
  });

  /* ---------- Change line quantity ---------- */
  async function changeLine(key, quantity) {
    try {
      const res = await fetch(routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) { toast('Could not update cart'); return; }
      await refreshCart();
      // On the dedicated cart page, reload to recompute totals/rows simply.
      if (document.body.classList.contains('template-cart')) window.location.reload();
    } catch (e) { toast('Could not update cart'); }
  }

  /* ---------- Quantity steppers (product + cart) ---------- */
  function bindQtySteppers(scope) {
    $$('[data-qty]', scope).forEach((wrap) => {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      const input = wrap.querySelector('input');
      const dec = wrap.querySelector('[data-qty-dec]');
      const inc = wrap.querySelector('[data-qty-inc]');
      const apply = (val) => {
        const min = parseInt(input.min || '1', 10);
        val = Math.max(min, val);
        input.value = val;
        const key = wrap.dataset.lineKey;
        if (key) changeLine(key, val);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (dec) dec.addEventListener('click', () => apply(parseInt(input.value, 10) - 1));
      if (inc) inc.addEventListener('click', () => apply(parseInt(input.value, 10) + 1));
      if (wrap.dataset.lineKey) {
        input.addEventListener('change', () => apply(parseInt(input.value, 10) || 1));
      }
    });
  }
  bindQtySteppers(document);

  /* ---------- Remove line ---------- */
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (!rm) return;
    e.preventDefault();
    changeLine(rm.dataset.lineRemove, 0);
  });

  /* ---------- Product: variants + gallery ---------- */
  function initProduct(root) {
    const dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;
    let product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const form = root.querySelector('form[data-product-form]');
    const idInput = form ? form.querySelector('[name="id"]') : null;
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare]');
    const addBtn = root.querySelector('[data-add-button]');
    const optionInputs = $$('[data-option-index]', root);

    function currentOptions() {
      const opts = [];
      optionInputs.forEach((inp) => {
        if (inp.type === 'radio') { if (inp.checked) opts[parseInt(inp.dataset.optionIndex, 10)] = inp.value; }
        else opts[parseInt(inp.dataset.optionIndex, 10)] = inp.value;
      });
      return opts;
    }
    function matchVariant(opts) {
      return product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    function render() {
      const v = matchVariant(currentOptions());
      if (!v) {
        if (addBtn) { addBtn.disabled = true; addBtn.querySelector('[data-add-label]').textContent = 'Unavailable'; }
        return;
      }
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.innerHTML = money(v.price);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.innerHTML = money(v.compare_at_price); compareEl.hidden = false;
        } else compareEl.hidden = true;
      }
      if (addBtn) {
        const label = addBtn.querySelector('[data-add-label]');
        addBtn.disabled = !v.available;
        if (label) label.textContent = v.available ? (addBtn.dataset.addText || 'Add to cart') : 'Sold out';
      }
      // swap gallery if variant has featured image
      if (v.featured_image && v.featured_image.src) {
        const main = root.querySelector('[data-main-image] img');
        if (main) main.src = v.featured_image.src;
      }
    }
    optionInputs.forEach((inp) => inp.addEventListener('change', render));
    render();

    // gallery thumbnails
    const main = root.querySelector('[data-main-image] img');
    $$('[data-thumb]', root).forEach((t) => {
      t.addEventListener('click', () => {
        if (main) main.src = t.dataset.full || t.querySelector('img').src;
        $$('[data-thumb]', root).forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
      });
    });
  }
  $$('[data-product-root]').forEach(initProduct);

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    $$('.reveal').forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Header shrink on scroll ---------- */
  const header = $('.header');
  if (header) {
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 20);
      last = y;
    }, { passive: true });
  }

  /* ---------- Newsletter inline feedback ---------- */
  document.addEventListener('submit', (e) => {
    const nf = e.target.closest('[data-newsletter]');
    if (!nf) return;
    // let Shopify handle the POST; just a tactile confirmation
    setTimeout(() => toast('Thank you — welcome to the Aurelium circle.'), 50);
  });
})();
